import React, { useEffect, useState } from 'react';
import { collection, getDocs, doc, addDoc, updateDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { HonorScore, HonorScoreType, Player } from '@/types';
import { Award, Trash2, Globe, EyeOff, ShieldCheck, CircleAlert } from 'lucide-react';

const TYPES: { value: HonorScoreType; label: string }[] = [
  { value: '300', label: '300 game' },
  { value: '299', label: '299 game' },
  { value: '298', label: '298 game' },
  { value: '800', label: '800 series' },
  { value: '700', label: '700 series' },
  { value: 'other', label: 'Other honor' },
];

interface HonorsTabProps {
  players: Player[];
}

export const HonorsTab: React.FC<HonorsTabProps> = ({ players }) => {
  const [items, setItems] = useState<HonorScore[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // create form
  const [playerId, setPlayerId] = useState('');
  const [bowlerName, setBowlerName] = useState('');
  const [type, setType] = useState<HonorScoreType>('300');
  const [score, setScore] = useState('300');
  const [label, setLabel] = useState('');
  const [date, setDate] = useState('');
  const [league, setLeague] = useState('');
  const [sanctioning, setSanctioning] = useState<HonorScore['sanctioning']>('USBC');
  const [verified, setVerified] = useState(false);

  // Willard team only — the program tracks other schools' kids, not our concern here
  const publishablePlayers = [...players]
    .filter((p) => p.removed !== true && p.willardTeam !== false)
    .sort((a, b) => a.name.localeCompare(b.name));

  const selectedPlayer = players.find((p) => p.id === playerId);
  // A linked minor can only go public once their profile is published+consented.
  const canBePublic = !playerId || !!(selectedPlayer?.published && selectedPlayer?.consentOnFile);

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      const snap = await getDocs(collection(db, 'honorScores'));
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data(), createdAt: d.data().createdAt?.toDate() || new Date() })) as HonorScore[];
      data.sort((a, b) => (b.score || 0) - (a.score || 0) || b.createdAt.getTime() - a.createdAt.getTime());
      setItems(data);
    } catch (e) {
      console.error('honorScores load failed', e);
      setError('Could not load honor scores.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setPlayerId('');
    setBowlerName('');
    setType('300');
    setScore('300');
    setLabel('');
    setDate('');
    setLeague('');
    setSanctioning('USBC');
    setVerified(false);
  };

  const handleAdd = async (makePublic: boolean) => {
    const name = playerId ? selectedPlayer?.name || '' : bowlerName.trim();
    if (!name) {
      setError('Pick a bowler or enter a name.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const numericScore = parseInt(score, 10);
      await addDoc(collection(db, 'honorScores'), {
        ...(playerId ? { playerId } : {}),
        bowlerName: name,
        type,
        ...(Number.isFinite(numericScore) ? { score: numericScore } : {}),
        ...(label.trim() ? { label: label.trim() } : {}),
        ...(date.trim() ? { date: date.trim() } : {}),
        ...(league.trim() ? { league: league.trim() } : {}),
        ...(sanctioning ? { sanctioning } : {}),
        verified,
        public: makePublic && canBePublic,
        createdAt: serverTimestamp(),
      });
      resetForm();
      await fetchItems();
    } catch (e) {
      console.error('add honor failed', e);
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const togglePublic = async (item: HonorScore) => {
    // Guard: don't expose a linked minor who isn't published.
    if (!item.public && item.playerId) {
      const p = players.find((x) => x.id === item.playerId);
      if (!(p?.published && p?.consentOnFile)) {
        setError(`${item.bowlerName} isn't published yet, publish their profile before making their honor public.`);
        return;
      }
    }
    const next = !item.public;
    const prev = items;
    setItems((list) => list.map((i) => (i.id === item.id ? { ...i, public: next } : i)));
    try {
      await updateDoc(doc(db, 'honorScores', item.id), { public: next });
    } catch (e) {
      console.error('toggle public failed', e);
      setItems(prev);
    }
  };

  const handleDelete = async (item: HonorScore) => {
    if (!window.confirm(`Delete ${item.bowlerName}'s ${item.type} record?`)) return;
    try {
      await deleteDoc(doc(db, 'honorScores', item.id));
      setItems((list) => list.filter((i) => i.id !== item.id));
    } catch (e) {
      console.error('delete honor failed', e);
      alert('Failed to delete.');
    }
  };

  const inputCls =
    'w-full px-4 py-3 border border-tiger-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-tiger-primary-black focus:border-transparent transition';

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl shadow-tiger-lg p-6">
        <h2 className="text-xl font-display font-black text-tiger-primary-black mb-1 flex items-center gap-2">
          <Award className="w-5 h-5" /> 300 Club &amp; Honor Scores
        </h2>
        <p className="text-sm text-tiger-neutral-500 mb-4">
          Perfect games, honor series, and milestones, for current bowlers, alumni, or coaches. Link a current bowler
          from the dropdown, or type any name (yourself, an alum). A linked player must be published before their honor
          can go public.
        </p>

        {error && <div className="p-4 mb-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>}

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-bold text-tiger-neutral-700 mb-2">Bowler (current team)</label>
            <select
              value={playerId}
              onChange={(e) => {
                setPlayerId(e.target.value);
                if (e.target.value) setBowlerName('');
              }}
              className={inputCls}
            >
              <option value="">Someone else (type a name)</option>
              {publishablePlayers.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                  {p.published && p.consentOnFile ? '' : ' (not published)'}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-bold text-tiger-neutral-700 mb-2">Or a name (alum / coach)</label>
            <input
              className={inputCls}
              value={bowlerName}
              onChange={(e) => setBowlerName(e.target.value)}
              disabled={!!playerId}
              placeholder="e.g. Coach Collins"
            />
          </div>
          <div>
            <label className="block text-sm font-bold text-tiger-neutral-700 mb-2">Honor</label>
            <select
              value={type}
              onChange={(e) => {
                const t = e.target.value as HonorScoreType;
                setType(t);
                if (['300', '299', '298'].includes(t)) setScore(t);
              }}
              className={inputCls}
            >
              {TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-bold text-tiger-neutral-700 mb-2">Score</label>
            <input className={inputCls} value={score} onChange={(e) => setScore(e.target.value)} inputMode="numeric" placeholder="300" />
          </div>
          <div>
            <label className="block text-sm font-bold text-tiger-neutral-700 mb-2">When</label>
            <input className={inputCls} value={date} onChange={(e) => setDate(e.target.value)} placeholder="2004, or 03/15/2025" />
          </div>
          <div>
            <label className="block text-sm font-bold text-tiger-neutral-700 mb-2">League / event</label>
            <input className={inputCls} value={league} onChange={(e) => setLeague(e.target.value)} placeholder="Junior/Major at Sunshine" />
          </div>
          <div>
            <label className="block text-sm font-bold text-tiger-neutral-700 mb-2">Sanctioning</label>
            <select value={sanctioning} onChange={(e) => setSanctioning(e.target.value as HonorScore['sanctioning'])} className={inputCls}>
              <option value="USBC">USBC</option>
              <option value="ABC">ABC (pre-2005)</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-bold text-tiger-neutral-700 mb-2">Label (optional)</label>
            <input className={inputCls} value={label} onChange={(e) => setLabel(e.target.value)} placeholder="First sanctioned 300" />
          </div>
        </div>

        <label className="flex items-center gap-2 mt-4 text-sm font-semibold text-tiger-neutral-700">
          <input type="checkbox" checked={verified} onChange={(e) => setVerified(e.target.checked)} className="w-4 h-4 accent-black" />
          <ShieldCheck className="w-4 h-4" /> I've verified this record
        </label>

        {!canBePublic && (
          <p className="flex items-center gap-2 mt-3 text-sm text-amber-700">
            <CircleAlert className="w-4 h-4" /> {selectedPlayer?.name} isn't published, you can save this now and make it
            public after you publish their profile.
          </p>
        )}

        <div className="flex gap-3 mt-5">
          <button
            onClick={() => handleAdd(false)}
            disabled={saving}
            className="px-6 py-3 border-2 border-tiger-neutral-300 text-tiger-neutral-700 font-bold rounded-lg hover:bg-tiger-neutral-100 disabled:opacity-50"
          >
            Save (hidden)
          </button>
          <button
            onClick={() => handleAdd(true)}
            disabled={saving || !canBePublic}
            className="px-6 py-3 bg-tiger-primary-black text-white font-bold rounded-lg hover:bg-tiger-neutral-800 disabled:opacity-50 flex items-center gap-2"
          >
            <Globe className="w-4 h-4" /> Save &amp; show publicly
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-tiger-lg overflow-hidden">
        <div className="p-6 border-b border-tiger-neutral-200">
          <h2 className="text-xl font-display font-black text-tiger-primary-black">Recorded Honors</h2>
        </div>
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-tiger-primary-black" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-12">
            <Award className="w-16 h-16 text-tiger-neutral-300 mx-auto mb-4" />
            <p className="text-tiger-neutral-600">No honor scores recorded yet</p>
          </div>
        ) : (
          <div className="divide-y divide-tiger-neutral-200">
            {items.map((item) => (
              <div key={item.id} className="p-6 flex flex-wrap items-center justify-between gap-4 hover:bg-tiger-neutral-50">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-display text-2xl text-tiger-primary-black">{item.score || item.type}</span>
                    <span className="font-bold text-tiger-primary-black">{item.bowlerName}</span>
                    {item.verified && (
                      <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full bg-green-100 text-green-800">
                        <ShieldCheck className="w-3 h-3" /> Verified
                      </span>
                    )}
                    <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-tiger-neutral-100 text-tiger-neutral-600">
                      {item.sanctioning || 'USBC'}
                    </span>
                  </div>
                  <p className="text-sm text-tiger-neutral-500 mt-0.5">
                    {[item.label, item.league, item.date].filter(Boolean).join(' · ') || item.type}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => togglePublic(item)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 ${
                      item.public ? 'bg-green-100 text-green-800 hover:bg-green-200' : 'bg-tiger-neutral-100 text-tiger-neutral-700 hover:bg-tiger-neutral-200'
                    }`}
                  >
                    {item.public ? (
                      <>
                        <Globe className="w-3.5 h-3.5" /> Public
                      </>
                    ) : (
                      <>
                        <EyeOff className="w-3.5 h-3.5" /> Hidden
                      </>
                    )}
                  </button>
                  <button onClick={() => handleDelete(item)} className="p-2 text-tiger-neutral-500 hover:text-red-600 hover:bg-red-50 rounded-lg" title="Delete">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
